import React, { useState } from 'react';
import { Share2, AlertCircle, Check, CreditCard } from 'lucide-react';
import { shareOnLinkedIn } from '../lib/linkedin';
import { getCurrentUser } from '../lib/auth';
import UpgradeModal from './UpgradeModal';

interface LinkedInShareButtonProps {
  content: string;
  className?: string;
}

const LinkedInShareButton: React.FC<LinkedInShareButtonProps> = ({ content, className = '' }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleShare = async () => {
    const user = getCurrentUser();
    if (!user) {
      setError('Vous devez être connecté pour partager sur LinkedIn');
      return;
    }

    // Check if user has Pro or Enterprise access
    if (!['pro', 'entreprise'].includes(user.role)) {
      setShowUpgradeModal(true);
      return;
    }

    setIsSharing(true);
    setError(null);
    setSuccess(false);

    try {
      await shareOnLinkedIn(content);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error: any) {
      setError(error.message || 'Erreur lors du partage sur LinkedIn');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleShare}
        disabled={isSharing}
        className={`flex items-center space-x-2 bg-[#0A66C2] text-white px-4 py-2 rounded-md hover:bg-[#004182] transition-colors disabled:opacity-50 ${className}`}
      >
        {isSharing ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
        ) : (
          <>
            <Share2 className="w-5 h-5" />
            <span>Partager sur LinkedIn</span>
          </>
        )}
      </button>

      {error && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-red-50 text-red-600 p-2 rounded-md flex items-center text-sm">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-green-50 text-green-600 p-2 rounded-md flex items-center text-sm">
          <Check className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>Partagé avec succès sur LinkedIn !</span>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-indigo-600" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Fonctionnalité Premium
              </h3>
              
              <p className="text-gray-600 mb-6">
                Le partage sur LinkedIn est disponible uniquement avec les forfaits Pro et Entreprise. Passez à un forfait supérieur pour débloquer cette fonctionnalité !
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Plus tard
                </button>
                <a
                  href="/subscription"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Voir les forfaits
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkedInShareButton;